class PremiumSplitStory extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.media = this.querySelector('[data-story-media]');
    this.image = this.media?.querySelector('img');
    this.pointerFrame = 0;

    this.onPointerDown = this.handlePointerDown.bind(this);
    this.onPointerMove = this.handlePointerMove.bind(this);
    this.onPointerLeave = this.resetMediaTilt.bind(this);

    this.addEventListener('pointerdown', this.onPointerDown);
    if (this.media && this.dataset.enableTilt === 'true' && !this.reduceMotion) {
      this.media.addEventListener('pointermove', this.onPointerMove);
      this.media.addEventListener('pointerleave', this.onPointerLeave);
    }

    if (this.image) {
      if (this.image.complete) this.classList.add('is-media-loaded');
      else this.image.addEventListener('load', this.handleImageLoad, { once: true });
    }

    requestAnimationFrame(() => this.classList.add('is-ready'));
    if (this.dataset.enableReveal !== 'true' || this.reduceMotion || !('IntersectionObserver' in window)) {
      this.dataset.revealed = 'true';
      return;
    }

    this.revealObserver = new IntersectionObserver(this.handleReveal, {
      rootMargin: '120px 0px',
      threshold: 0.1,
    });
    this.revealObserver.observe(this);
  }

  disconnectedCallback() {
    this.revealObserver?.disconnect();
    this.removeEventListener('pointerdown', this.onPointerDown);
    this.media?.removeEventListener('pointermove', this.onPointerMove);
    this.media?.removeEventListener('pointerleave', this.onPointerLeave);
    this.image?.removeEventListener('load', this.handleImageLoad);
    if (this.pointerFrame) cancelAnimationFrame(this.pointerFrame);
  }

  handleReveal = (entries) => {
    if (!entries[0]?.isIntersecting) return;
    this.dataset.revealed = 'true';
    this.revealObserver?.disconnect();
  };

  handleImageLoad = () => {
    this.classList.add('is-media-loaded');
  };

  handlePointerDown(event) {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    const target = event.target.closest?.('[data-story-interactive]');
    if (!target || !this.contains(target) || this.reduceMotion) return;

    target.classList.remove('is-clicked');
    requestAnimationFrame(() => {
      target.classList.add('is-clicked');
      window.setTimeout(() => target.classList.remove('is-clicked'), 520);
    });
  }

  handlePointerMove(event) {
    if (!this.media || event.pointerType === 'touch') return;
    const rect = this.media.getBoundingClientRect();
    const x = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
    const y = Math.max(0, Math.min(1, (event.clientY - rect.top) / rect.height));
    const strength = Number(this.dataset.tiltStrength) || 3;

    if (this.pointerFrame) cancelAnimationFrame(this.pointerFrame);
    this.pointerFrame = requestAnimationFrame(() => {
      this.media.style.setProperty('--pss-tilt-x', `${(0.5 - y) * strength}deg`);
      this.media.style.setProperty('--pss-tilt-y', `${(x - 0.5) * strength}deg`);
      this.media.style.setProperty('--pss-light-x', `${x * 100}%`);
      this.media.style.setProperty('--pss-light-y', `${y * 100}%`);
    });
  }

  resetMediaTilt() {
    if (!this.media) return;
    this.media.style.setProperty('--pss-tilt-x', '0deg');
    this.media.style.setProperty('--pss-tilt-y', '0deg');
    this.media.style.setProperty('--pss-light-x', '50%');
    this.media.style.setProperty('--pss-light-y', '50%');
  }
}

if (!customElements.get('premium-split-story')) {
  customElements.define('premium-split-story', PremiumSplitStory);
}
